import { Redirect, Route, Switch, useLocation } from "wouter";
import { useAuth } from "./context/AuthContext.jsx";
import usePageTitle from "./hooks/usePageTitle.js";
import TopBar from "./components/TopBar.jsx";
import Login from "./pages/Login.jsx";
import Signup from "./pages/Signup.jsx";
import ForgotPassword from "./pages/ForgotPassword.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import PublicDiagnostic from "./pages/PublicDiagnostic.jsx";
import Landing from "./pages/Landing.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import Diagnostic from "./pages/Diagnostic.jsx";
import Ground from "./pages/Ground.jsx";
import Playbook from "./pages/Playbook.jsx";
import CoachHub from "./pages/CoachHub.jsx";
import CoachSession from "./pages/CoachSession.jsx";
import Record from "./pages/Record.jsx";
import Exports from "./pages/Exports.jsx";
import Integrations from "./pages/Integrations.jsx";
import Insight from "./pages/Insight.jsx";
import Share from "./pages/Share.jsx";
import PublicShare from "./pages/PublicShare.jsx";
import Account from "./pages/Account.jsx";
import Admin from "./pages/Admin.jsx";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (user === null) return <Redirect to="/login" />;
  return children;
}

const TITLES = [
  { test: (l) => l === "/login", title: "Log in" },
  { test: (l) => l === "/signup", title: "Sign up" },
  { test: (l) => l === "/forgot-password", title: "Forgot password" },
  { test: (l) => l === "/reset-password", title: "Reset password" },
  { test: (l) => l === "/take-the-diagnostic", title: "Take the Standing Check" },
  { test: (l) => l === "/", title: null },
  { test: (l) => l.startsWith("/shared/"), title: "Shared" },
  { test: (l) => l === "/diagnostic", title: "The Standing Check" },
  { test: (l) => l === "/ground", title: "Your Ground" },
  { test: (l) => l === "/floor", title: "The Floor" },
  { test: (l) => l === "/playbook", title: "The Playbook" },
  { test: (l) => l === "/coach", title: "Your Corner" },
  { test: (l) => l.startsWith("/coach/"), title: "Your Corner" },
  { test: (l) => l === "/record", title: "The Record" },
  { test: (l) => l === "/exports", title: "Exports" },
  { test: (l) => l === "/integrations", title: "Integrations" },
  { test: (l) => l === "/insight", title: "Insight" },
  { test: (l) => l === "/share", title: "Share" },
  { test: (l) => l === "/account", title: "Account" },
  { test: (l) => l === "/admin", title: "Admin" },
];

function titleForLocation(location, user) {
  if (location === "/" && user) return "Home";
  const match = TITLES.find(({ test }) => test(location));
  return match ? match.title : null;
}

export default function App() {
  const { user } = useAuth();
  const [location] = useLocation();

  usePageTitle(titleForLocation(location, user));

  if (user === undefined) {
    return null;
  }

  if (location === "/login") {
    if (user) return <Redirect to="/" />;
    return <Login />;
  }

  if (location === "/signup") {
    if (user) return <Redirect to="/" />;
    return <Signup />;
  }

  if (location === "/forgot-password") {
    if (user) return <Redirect to="/" />;
    return <ForgotPassword />;
  }

  if (location === "/reset-password") {
    if (user) return <Redirect to="/" />;
    return <ResetPassword />;
  }

  if (location === "/take-the-diagnostic") {
    return <PublicDiagnostic />;
  }

  if (location === "/" && !user) {
    return <Landing />;
  }

  if (location.startsWith("/shared/")) {
    return (
      <Switch>
        <Route path="/shared/:token" component={PublicShare} />
      </Switch>
    );
  }

  return (
    <Protected>
      <TopBar />
      <Switch>
        <Route path="/" component={Dashboard} />
        <Route path="/diagnostic" component={Diagnostic} />
        <Route path="/ground" component={Ground} />
        <Route path="/floor">
          <Redirect to="/ground" />
        </Route>
        <Route path="/playbook" component={Playbook} />
        <Route path="/coach" component={CoachHub} />
        <Route path="/coach/:id" component={CoachSession} />
        <Route path="/record" component={Record} />
        <Route path="/exports" component={Exports} />
        <Route path="/integrations" component={Integrations} />
        <Route path="/insight" component={Insight} />
        <Route path="/share" component={Share} />
        <Route path="/account" component={Account} />
        <Route path="/admin" component={Admin} />
        <Route>
          <div className="page">Not found.</div>
        </Route>
      </Switch>
    </Protected>
  );
}
